import type { Bounds, SceneEffect } from '@manudev/assistant';
import * as THREE from 'three';
import { useHeroData, type PropsElement } from '@/hero/data';
import type { LayerHandle } from '@/hero/GlbLayer';
import { elementBox } from '@/render/batch';
import { MODELS_BASE } from '@/hero/preload';
import { useSelectionStore } from '@/state/selection';
import { hero } from '@/state/tour';
import { useHeroStore } from '@/store/store';
import { layersOf, resetLayers, type StationImpl } from '../types';
import { AskAboutCard } from './Card';
import { AssistantPanel } from './Panel';
import { Labels } from './Scene';

let lastHighlights: number[] = [];
let lastEffects: SceneEffect[] = [];
let isolated = false;
// StoreLayer registers the bounds of what build just made a frame or two later, so the camera
// move (and the type labels that ride along with it) wait for them rather than firing on a
// box that does not exist yet.
let awaitingFocus: PropsElement[] = [];
const LABEL_MS = 4000;

export const build: StationImpl = {
  pick: 'all',
  enter({ layers, height }) {
    resetLayers(layers, height);
    for (const h of layersOf(layers, (d) => d === 'space')) h.rig.setVisible(true);
    for (const h of layersOf(layers, (d) => d === 'mep')) h.rig.setVisible(false);
    lastHighlights = [];
    lastEffects = [];
    isolated = false;
    awaitingFocus = [];
    const store = useSelectionStore.getState();
    if (!store.bounds) store.setBounds(measure(layers));
    if (hero.ifc) useHeroStore.getState().load(MODELS_BASE + hero.ifc);
  },
  update({ layers, height }) {
    const { highlighted, effects } = useSelectionStore.getState();
    if (effects !== lastEffects) {
      lastEffects = effects;
      applyEffects(effects, layers, height);
    }
    if (highlighted !== lastHighlights) {
      lastHighlights = highlighted;
      applyHighlights(highlighted, layers);
    }
    if (awaitingFocus.length) focusWhenMeasured(layers, height);
  },
  leave({ layers, height }) {
    resetLayers(layers, height);
    for (const h of layers.values()) {
      h.rig.clearColors();
      h.rig.clearVisibility();
    }
    const store = useSelectionStore.getState();
    store.setHighlighted([]);
    store.setEffects([]);
    store.setFocus(null);
    store.setLabels([]);
  },
  Panel: AssistantPanel,
  Scene: Labels,
  Card: AskAboutCard,
};

function applyHighlights(ids: number[], layers: Map<string, LayerHandle>) {
  const set = new Set(ids);
  for (const h of layers.values()) {
    if (set.size === 0) {
      h.rig.clearStates();
      continue;
    }
    for (const el of h.rig.elements.values()) {
      const state = set.has(el.expressID)
        ? 'highlight'
        : h.model.discipline === 'space' || isolated
          ? null
          : 'dim';
      h.rig.setElementState(el.expressID, state);
    }
  }
}

function applyEffects(effects: SceneEffect[], layers: Map<string, LayerHandle>, height: number) {
  const store = useSelectionStore.getState();
  const all = [...layers.values()];
  const clear = () => {
    for (const h of all) {
      h.rig.clearColors();
      h.rig.clearVisibility();
      h.rig.setClipY(height + 2);
    }
    isolated = false;
    store.setFocus(null);
    store.setLabels([]);
  };
  if (effects.length === 0) return;
  // every answer starts from a clean view, then applies its own effects
  clear();
  if (effects.some((e) => e.kind === 'reset')) {
    store.setHighlighted([]);
    return;
  }
  for (const e of effects) {
    switch (e.kind) {
      case 'focus':
        store.setFocus({ center: e.center, radius: e.radius });
        break;
      case 'isolate': {
        const keep = new Set(e.ids);
        for (const h of all) {
          if (h.model.discipline === 'space') continue;
          for (const el of h.rig.elements.values())
            h.rig.setElementVisible(el.expressID, keep.has(el.expressID));
        }
        isolated = true;
        break;
      }
      case 'section':
        for (const h of all) h.rig.setClipY(e.y);
        break;
      case 'colour': {
        const painted = new Set<number>();
        for (const g of e.groups) for (const id of g.ids) painted.add(id);
        for (const h of all) {
          if (h.model.discipline === 'space') continue;
          for (const el of h.rig.elements.values())
            if (!painted.has(el.expressID)) h.rig.setElementState(el.expressID, 'dim');
        }
        for (const g of e.groups)
          for (const id of g.ids) for (const h of all) h.rig.setElementColor(id, g.color);
        break;
      }
      case 'label':
        store.setLabels(e.items);
        break;
      case 'build': {
        const { elements, newIds } = useHeroStore.getState();
        const data = useHeroData.getState();
        data.sync(elements, newIds);
        for (const h of all) h.rig.setRemoved(data.removed);
        // StoreLayer mounts these from useHeroStore.newIds; the camera and labels follow once measured
        awaitingFocus = e.created;
        break;
      }
    }
  }
}

function focusWhenMeasured(layers: Map<string, LayerHandle>, height: number) {
  const store = useSelectionStore.getState();
  const bounds = store.bounds;
  if (!bounds) return;
  const ready = awaitingFocus.filter((el) => bounds[el.expressID]);
  if (!ready.length) return;
  // the hero grew with the build, so the cut from the answer's clean view moves up with it
  for (const h of layers.values()) h.rig.setClipY(height + 2);
  const box: Bounds = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
  for (const el of ready) {
    const b = bounds[el.expressID]!;
    for (let k = 0; k < 3; k++) {
      box[k] = Math.min(box[k]!, b[k]!);
      box[k + 3] = Math.max(box[k + 3]!, b[k + 3]!);
    }
  }
  awaitingFocus = [];
  const center: [number, number, number] = [
    (box[0] + box[3]) / 2,
    (box[1] + box[4]) / 2,
    (box[2] + box[5]) / 2,
  ];
  const radius = Math.hypot(box[3] - box[0], box[4] - box[1], box[5] - box[2]) / 2;
  store.setFocus({ center, radius: Math.max(radius, 1) });

  const items = ready.map((el) => ({ id: el.expressID, text: el.type.replace(/^Ifc/, '') }));
  store.setLabels(items);
  window.setTimeout(() => {
    if (useSelectionStore.getState().labels === items) store.setLabels([]);
  }, LABEL_MS);
}

// World-space box per element, once per hero: what scene focus/section and elements_near use.
function measure(layers: Map<string, LayerHandle>): Record<number, Bounds> {
  const out: Record<number, Bounds> = {};
  const box = new THREE.Box3();
  for (const h of layers.values()) {
    for (const el of h.rig.elements.values()) {
      if (elementBox(el, box).isEmpty()) continue;
      out[el.expressID] = [box.min.x, box.min.y, box.min.z, box.max.x, box.max.y, box.max.z];
    }
  }
  return out;
}
